import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import BordTooltip from 'components/BordTooltip/BordTooltip';
import { IOrderPriceEditionForm } from '../../OrderPriceEdition.types';
import { BordOneToneIcon, NudosMaskedNumberInput } from 'components/NudosComponents';
import { nudosTextcolor } from 'styles/styles.constants';
import { CURRENCIES } from 'types/bussines';
import { formatCost } from 'utils/formatNumbers';
import { getOrderProductConfigUsdAmount } from 'utils/orderPriceEdition';

import './OrderEditionTotals.scss';

interface IOrderEditionTotalsProps {
  currency: string;
  hasTaxes: boolean;
  hasCommission: boolean;
  taxRate: number;
}

const OrderEditionTotals = ({ currency, hasTaxes, hasCommission, taxRate }: IOrderEditionTotalsProps) => {
  const { setValue, control } = useFormContext<IOrderPriceEditionForm>();

  const [
    sales = [],
    iva = 0,
    total = 0,
    totalLocal = 0,
    commissionUsd = 0,
    exchangeRate = 0,
    shippingValueUsd = 0,
    shipmentTaxUsd = 0
  ] =
    useWatch({
      control,
      name: [
        'sales',
        'iva',
        'totalUsd',
        'totalLocal',
        'commissionUsd',
        'exchangeRate',
        'shippingValueUsd',
        'shipmentTaxUsd'
      ]
    }) || [];

  const subtotalCalculated = useMemo(() => {
    return sales.reduce((acc, sale) => {
      const saleUsd = Number(sale?.saleValueUsd) || 0;
      const configsUsd = (sale?.orderProductPrices || []).reduce(
        (sum, productConfig) => sum + getOrderProductConfigUsdAmount(productConfig),
        0
      );
      return acc + saleUsd + configsUsd;
    }, 0);
  }, [sales]);

  /**
   * Calcula el importe de IVA a partir de un subtotal que ya lo incluye.
   */
  const taxesCalculated = useMemo(() => {
    if (!hasTaxes || subtotalCalculated <= 0) {
      return 0;
    }
    const netAmount = subtotalCalculated / (1 + taxRate);
    return subtotalCalculated - netAmount;
  }, [hasTaxes, subtotalCalculated, taxRate]);

  /**
   * Calcula el total final en USD considerando comisiones, envíos e impuesto sobre envío.
   */
  const totalUsdCalculated = useMemo(() => {
    const netSubtotal = hasTaxes ? subtotalCalculated - taxesCalculated : subtotalCalculated;
    const shipmentTax = Number(shipmentTaxUsd) || 0;
    return netSubtotal + commissionUsd + shippingValueUsd + shipmentTax;
  }, [subtotalCalculated, taxesCalculated, commissionUsd, shippingValueUsd, shipmentTaxUsd, hasTaxes]);

  /**
   * Actualiza el total en moneda local (si aplica)
   * aplicando el tipo de cambio a las comisiones y envíos
   */
  const totalLocalCalculated = useMemo(() => {
    if (currency === CURRENCIES.USD) return 0;
    const salesTotalLocal = sales.reduce((acc, sale) => {
      const saleLocal = Number(sale?.saleValue) || 0;
      const configsLocal = (sale?.orderProductPrices || []).reduce(
        (sum, productConfig) => sum + (Number(productConfig?.totalValueLocal) || 0),
        0
      );
      return acc + saleLocal + configsLocal;
    }, 0);
    const netLocalSales = hasTaxes ? salesTotalLocal - taxesCalculated * exchangeRate : salesTotalLocal;
    const shipmentTax = Number(shipmentTaxUsd) || 0;
    return netLocalSales + (shippingValueUsd + commissionUsd + shipmentTax) * exchangeRate;
  }, [sales, commissionUsd, shippingValueUsd, shipmentTaxUsd, exchangeRate, currency, hasTaxes, taxesCalculated]);

  const applyCalculatedTotalsToForm = () => {
    setValue('totalUsd', totalUsdCalculated);
    setValue('iva', hasTaxes ? taxesCalculated : 0);
    setValue('subtotal', subtotalCalculated);

    if (currency !== CURRENCIES.USD) {
      setValue('totalLocal', totalLocalCalculated);
    }
  };

  useEffect(() => {
    applyCalculatedTotalsToForm();
  }, [totalUsdCalculated, taxesCalculated, totalLocalCalculated, currency, iva, subtotalCalculated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="editionTotals">
      <p className="subtotal">
        <span>Subtotal</span>
        <span>${formatCost(Number(subtotalCalculated))} USD</span>
      </p>
      <p className="subtotalShipping">
        <span>Subtotal de envíos</span>
        <span>${formatCost(Number(shippingValueUsd))} USD</span>
      </p>
      {hasTaxes && (
        <div className="iva">
          <div className="ivaLabel">
            IVA (-{taxRate * 100}%) &nbsp;
            <BordTooltip
              message={
                <>
                  <br />
                  Para las razones sociales en Zona Franca <br /> no cobramos el impuesto de {taxRate * 100}%
                </>
              }
            >
              <BordOneToneIcon variant="info" customWidth="12px" stroke={nudosTextcolor} standardSize={52} />
            </BordTooltip>
          </div>
          <span>${iva.toFixed(2)} USD</span>
        </div>
      )}
      {hasCommission && (
        <div className="paymentCommission">
          <span>Comisión de pago</span>
          <Controller
            name="commissionUsd"
            control={control}
            render={({ field }) => (
              <NudosMaskedNumberInput
                isFilled={true}
                placeholder={`${field.value ? field.value : '0.00'} USD`}
                handleChange={text => field.onChange(Number(text || '0'))}
                currentValue={String(field.value)}
                maskPrefix="$"
                maskSufix=" USD"
                numberOfDecimals={2}
                customClassName="paymentCommissionInput"
                inputHeight={32}
                inputPadding={8}
              />
            )}
          />
        </div>
      )}
      <hr />
      <p className="totalUsd">
        <span>Total a pagar en USD</span>
        <span>${formatCost(Number(total))} USD</span>
      </p>
      {currency !== CURRENCIES.USD && (
        <p className="totalLocal">
          <span>Total a pagar en {currency}</span>
          <span>
            ${formatCost(Number(totalLocal))} {currency}
          </span>
        </p>
      )}
    </section>
  );
};

export default OrderEditionTotals;
